import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { attachmentRecords, customerPos, customers } from '../api/methods';
export const customerKeys = {
    list: (params) => ['customer', 'list', params],
    summary: (id) => ['customer', 'summary', id],
    pos: (id, includeInactive = false) => ['customer', 'pos', id, { includeInactive }],
    accountOwners: () => ['customer', 'account-owners'],
    activity: (id) => ['customer', 'activity', id],
    attachments: (id) => ['customer', 'attachments', id],
};
/** Paginated, searchable customer directory (Sales › Customers tab). */
export function useCustomerDirectory(params = {}) {
    return useQuery({
        queryKey: customerKeys.list(params),
        queryFn: () => customers.list(params),
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });
}
/** Customer 360 — record + estimates + invoices + tickets + totals + local meta. */
export function useCustomerSummary(qbCustomerId, enabled = true) {
    return useQuery({
        queryKey: customerKeys.summary(qbCustomerId ?? ''),
        queryFn: () => customers.getSummary(qbCustomerId).then((r) => r.summary),
        enabled: enabled && !!qbCustomerId,
        staleTime: 30_000,
    });
}
export function useCustomerAttachments(qbCustomerId, options = {}) {
    const limit = options.limit ?? 50;
    const offset = options.offset ?? 0;
    return useQuery({
        queryKey: [...customerKeys.attachments(qbCustomerId ?? ''), { limit, offset }],
        queryFn: () => customers.getAttachments(qbCustomerId, { limit, offset }),
        enabled: !!qbCustomerId,
    });
}
export function useUploadCustomerAttachment(qbCustomerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (formData) => customers.uploadAttachment(qbCustomerId, formData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: customerKeys.attachments(qbCustomerId) });
            queryClient.invalidateQueries({ queryKey: customerKeys.summary(qbCustomerId) });
            queryClient.invalidateQueries({ queryKey: ['admin', 'customer-tax-rules'] });
        },
    });
}
export function useUpdateCustomerAttachment(qbCustomerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ attachmentId, data }) => (attachmentRecords.updateCompliance(attachmentId, data)),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: customerKeys.attachments(qbCustomerId) });
            queryClient.invalidateQueries({ queryKey: customerKeys.summary(qbCustomerId) });
            queryClient.invalidateQueries({ queryKey: ['admin', 'customer-tax-rules'] });
        },
    });
}
/**
 * Rename a customer document (#872). Display-only: PATCH /attachments/:id with
 * `filename` alone leaves storage_path untouched, so existing object URLs keep
 * working. Kept as a sibling of useUpdateCustomerAttachment rather than folded
 * into it — the compliance mutation sends compliance_type + expires_on together,
 * and merging the two would make every rename re-assert a compliance type.
 */
export function useRenameCustomerAttachment(qbCustomerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ attachmentId, filename }) => attachmentRecords.rename(attachmentId, filename),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: customerKeys.attachments(qbCustomerId) });
        },
    });
}
export function useDeleteCustomerAttachment(qbCustomerId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (attachmentId) => attachmentRecords.delete(attachmentId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: customerKeys.attachments(qbCustomerId) });
            queryClient.invalidateQueries({ queryKey: customerKeys.summary(qbCustomerId) });
            queryClient.invalidateQueries({ queryKey: ['admin', 'customer-tax-rules'] });
        },
    });
}
export function useCustomerPos(qbCustomerId, includeInactive = false, enabled = true) {
    return useQuery({
        queryKey: customerKeys.pos(qbCustomerId ?? '', includeInactive),
        queryFn: () => customerPos.list({ qbCustomerId: qbCustomerId, includeInactive }).then((r) => r.customer_pos),
        enabled: enabled && !!qbCustomerId,
        staleTime: 30_000,
    });
}
export function useCreateCustomerPo(qbCustomerId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (data) => customerPos.create({ ...data, qb_customer_id: data.qb_customer_id || qbCustomerId }).then((r) => r.customer_po),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['customer', 'pos', qbCustomerId] });
        },
    });
}
export function useUpdateCustomerPo(qbCustomerId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => customerPos.update(id, data).then((r) => r.customer_po),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['customer', 'pos', qbCustomerId] });
        },
    });
}
export function useSetCustomerPoStatus(qbCustomerId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, status }) => customerPos.setStatus(id, status).then((r) => r.customer_po),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['customer', 'pos', qbCustomerId] });
        },
    });
}
export function useRetryCustomerPoBaserow(qbCustomerId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => customerPos.retryBaserow(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['customer', 'pos', qbCustomerId] });
        },
    });
}
/** Save the customer's full Alturex-local CRM field set (never synced to QBO). */
export function useUpdateCustomerMeta() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => customers.updateMeta(id, data).then((r) => r.meta),
        onSuccess: (_meta, { id }) => qc.invalidateQueries({ queryKey: customerKeys.summary(id) }),
    });
}
/** Assignable account owners for the CRM owner picker. */
export function useAccountOwners(enabled = true) {
    return useQuery({
        queryKey: customerKeys.accountOwners(),
        queryFn: () => customers.accountOwners().then((r) => r.owners),
        enabled,
        staleTime: 5 * 60_000,
    });
}
/**
 * Write the QBO identity fields back to QuickBooks (Manager/Admin). This is a real
 * QBO mutation; invalidates the 360 so the refreshed record shows immediately.
 */
export function useUpdateCustomerQbo() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => customers.updateQbo(id, data).then((r) => r.customer),
        onSuccess: (_customer, { id }) => qc.invalidateQueries({ queryKey: customerKeys.summary(id) }),
    });
}
/** Write an all-state app tax treatment to QBO's global customer Taxable flag. */
export function useUpdateCustomerTaxStatusQbo() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (qbCustomerId) => customers.updateTaxStatusQbo(qbCustomerId),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['customer', 'summary'] }),
    });
}
/**
 * Read-only Ayrow CRM read-back for a customer (activity feed + contacts + open
 * follow-ups). Fails soft on the backend, so this never blocks the 360 page.
 */
export function useCustomerActivity(qbCustomerId, enabled = true) {
    return useQuery({
        queryKey: customerKeys.activity(qbCustomerId ?? ''),
        queryFn: () => customers.activity(qbCustomerId).then((r) => r.activity),
        enabled: enabled && !!qbCustomerId,
        staleTime: 60_000,
    });
}
//# sourceMappingURL=useCustomerSummary.js.map